import { Component, Inject, OnInit } from '@angular/core';

import { PrintProductionDto } from '../../../../../model/print-production/print-production-dto';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  DimensionType,
  MaterialType,
  NbColorsType,
  PrintMethodType,
  PrintProductTypes,
  StickerSubTypes,
  VariaSubTypes,
} from 'src/app/model/print-production/print-product-enums';
import { PrintProductionRequestService } from '../service/print-production-request.service';
import { SnackService } from '../../../../../services/snack.service';
import { PrintProductionProductOptionsService } from '../service/print-production-product-options.service';

@Component({
  selector: 'app-edit-print-production-modal',
  templateUrl: './edit-print-production-modal.component.html',
  styleUrls: ['./edit-print-production-modal.component.scss'],
})
export class EditPrintProductionModalComponent implements OnInit {
  protected readonly NbColorsType = NbColorsType;
  localPrintProduction: PrintProductionDto;
  defaultSecondaryColorOption: string = 'CYMK';
  isFormValid: boolean = false;

  printProductTypeChoices = this.mapEnumToChoices(PrintProductTypes);
  immoSubTypeChoices = this.mapEnumToChoices(StickerSubTypes);
  variaSubTypeChoices = this.mapEnumToChoices(VariaSubTypes);
  subTypeChoices: { key: string; value: any }[];
  printMethodTypeChoices: { key: string; value: string }[] = [];
  nbColorsTypeChoices: { key: string; value: string }[] = [];
  materialOptions: { key: string; value: string }[] = [];
  dimensionOptions: { key: string; value: string }[] = [];

  isSubTypeDisabled: boolean = true;
  isFilmcostDisabled: boolean = true;
  isSieveChangeDisabled: boolean = true;
  isDimensionsDisabled: boolean = true;
  isMaterialsDisabled: boolean = true;
  isPrintColorsDisabled: boolean = true;
  isCustomDimensionsDisabled: boolean = true;
  isPrintMethodDisabled: boolean = true;

  isShowMaterialComment: boolean = false;
  isShowDimensionsComment: boolean = false;
  isShowSecondaryColours: boolean = false;
  isShowSubTypeComment: boolean = false;

  mapEnumToChoices(enumType: object) {
    return Object.entries(enumType).map(([key, value]) => ({
      key,
      value,
    }));
  }

  constructor(
    public dialogRef: MatDialogRef<EditPrintProductionModalComponent>,
    @Inject(MAT_DIALOG_DATA) public printProduction: PrintProductionDto,
    private printProductionRequestService: PrintProductionRequestService,
    private snackService: SnackService,
    private optionsService: PrintProductionProductOptionsService
  ) {}

  ngOnInit(): void {
    this.localPrintProduction = new PrintProductionDto(this.printProduction);
    this.updatePrintMethodOptions();
    this.updateFilmcostAndSieveChangeDisabled();
    this.updateCustomDimensionsDisabled();
    this.updateIsShowMaterialComment();
    this.updateSubTypeDisabled();
    this.updateMaterialsAndDimensions();
    this.updateShowDimensionsComment();
    this.updatePrintMethodDisabled();
    this.updateMaterialsAndDimensionsDisabled();
    this.updateIsShowSubTypeComment();
    this.validateFields();
  }

  onTypeChange() {
    this.updatePrintMethodDisabled();
    this.updatePrintMethodOptions();
    this.updateFilmcostAndSieveChangeDisabled();
    this.updateCustomDimensionsDisabled();
    this.updateIsShowMaterialComment();
    this.updateSubTypeDisabled();
    this.updateMaterialsAndDimensions();
    this.updateMaterialsAndDimensionsDisabled();
    this.updateShowDimensionsComment();
    this.updateIsShowSubTypeComment();
    this.validateFields();
  }

  onSubTypeChange() {
    this.updateMaterialsAndDimensions();
    this.updateMaterialsAndDimensionsDisabled();
    this.updateIsShowSubTypeComment();
    this.validateFields();
  }

  onPrintMethodChange() {
    this.updateNbColorsOptions();
    this.updatePrintColorDisabled();
    this.validateFields();
  }

  onDimensionsChange() {
    this.updateCustomDimensionsDisabled();
    this.validateFields();
  }

  onMaterialChange() {
    this.updateIsShowMaterialComment();
    this.validateFields();
  }

  private updateMaterialsAndDimensions() {
    this.updateMaterials();
    this.updateDimensions();
    this.updateCustomDimensionsDisabled();
  }

  private updateMaterials() {
    const { type, subType } = this.localPrintProduction;
    const materials = this.optionsService.getAvailableMaterials(type, subType);
    this.materialOptions = this.mapEnumToChoices(MaterialType).filter(
      (option) =>
        materials.includes(
          MaterialType[option.key as keyof typeof MaterialType]
        )
    );

    if (
      !this.materialOptions.some(
        (option) => option.key === this.localPrintProduction.material
      )
    ) {
      this.localPrintProduction.material = null;
    }
  }

  private updateDimensions() {
    const { type, subType } = this.localPrintProduction;
    const dimensions = this.optionsService.getAvailableDimensions(
      type,
      subType
    );
    this.dimensionOptions = this.mapEnumToChoices(DimensionType).filter(
      (option) =>
        dimensions.includes(
          DimensionType[option.key as keyof typeof DimensionType]
        )
    );

    if (
      !this.dimensionOptions.some(
        (option) => option.key === this.localPrintProduction.dimensions
      )
    ) {
      this.localPrintProduction.dimensions = null;
    }
  }

  private updatePrintMethodOptions() {
    if (this.localPrintProduction.type) {
      const availablePrintMethods =
        this.optionsService.getAvailablePrintMethods(
          this.localPrintProduction.type
        );
      this.printMethodTypeChoices = this.mapEnumToChoices(
        PrintMethodType
      ).filter((option) =>
        availablePrintMethods.includes(
          PrintMethodType[option.key as keyof typeof PrintMethodType]
        )
      );

      if (
        !this.printMethodTypeChoices.some(
          (option) => option.key === this.localPrintProduction.printMethod
        )
      ) {
        this.localPrintProduction.printMethod = null;
      }
    } else {
      this.printMethodTypeChoices = [];
      this.localPrintProduction.printMethod = null;
    }

    this.updatePrintColorDisabled();
    this.updateNbColorsOptions();
  }

  private updateNbColorsOptions() {
    if (this.localPrintProduction.printMethod) {
      const printMethod = this.localPrintProduction.printMethod;
      const colors = this.optionsService.getAvailableColors(printMethod);
      this.nbColorsTypeChoices = this.mapEnumToChoices(NbColorsType).filter(
        (option) => colors.includes(option.value as NbColorsType)
      );
    }
    this.updateFilmcostAndSieveChangeDisabled();
  }

  private updateFilmcostAndSieveChangeDisabled() {
    const type = this.localPrintProduction.type;
    const subType = this.localPrintProduction.subType;
    const printMethod = this.localPrintProduction.printMethod;
    let isImmoSign = type === 'IMMO_IP_SIGN';
    let isStandardSticker =
      type === 'STICKER' && subType === 'IMMO_STANDARD_STICKER';
    let isScreenPrint =
      printMethod === 'SCREENPRINT' || printMethod === 'SCREENPRINT_DIGITAL';

    this.isFilmcostDisabled = this.isSieveChangeDisabled = !(
      (isImmoSign || isStandardSticker) &&
      isScreenPrint
    );

    if (this.isFilmcostDisabled) this.localPrintProduction.filmCost = null;
    if (this.isSieveChangeDisabled)
      this.localPrintProduction.sieveChange = null;
  }

  private updateCustomDimensionsDisabled() {
    const dimensionsType = this.localPrintProduction.dimensions;
    this.isCustomDimensionsDisabled = dimensionsType !== 'CUSTOM';
    if (this.isCustomDimensionsDisabled) {
      this.localPrintProduction.customDimensions.height = null;
      this.localPrintProduction.customDimensions.width = null;
    }
  }

  private updateIsShowMaterialComment() {
    this.isShowMaterialComment = this.localPrintProduction.material === 'OTHER';
    if (!this.isShowMaterialComment) {
      this.localPrintProduction.otherMaterialComment = null;
    }
  }

  private updateMaterialsAndDimensionsDisabled(): void {
    const { type, subType } = this.localPrintProduction;

    const isTypeEmpty = type == null;
    const isTypeSticker = type === 'STICKER';
    const isSubTypeEmpty = subType == null;

    this.isDimensionsDisabled = this.isMaterialsDisabled =
      isTypeEmpty || (isTypeSticker && isSubTypeEmpty);
  }

  private updateSubTypeDisabled() {
    if (this.localPrintProduction.type === 'STICKER') {
      this.isSubTypeDisabled = false;
      this.subTypeChoices = this.immoSubTypeChoices;
    } else if (this.localPrintProduction.type === 'VARIA') {
      this.isSubTypeDisabled = false;
      this.subTypeChoices = this.variaSubTypeChoices;
    } else {
      this.localPrintProduction.subType = null;
      this.isSubTypeDisabled = true;
      this.subTypeChoices = null;
    }
  }

  private updateIsShowSubTypeComment() {
    this.isShowSubTypeComment = this.localPrintProduction.subType === 'OTHER';
    if (!this.isShowSubTypeComment) {
      this.localPrintProduction.subTypeComment = null;
    }
  }

  private updateShowDimensionsComment() {
    let isVaria = this.localPrintProduction.type === 'VARIA';
    this.isShowDimensionsComment = isVaria;

    if (isVaria) {
      this.localPrintProduction.dimensions = 'CUSTOM';
      this.updateCustomDimensionsDisabled();
    } else {
      this.localPrintProduction.dimensionsComment = null;
    }
  }

  private updatePrintColorDisabled() {
    let printMethod = this.localPrintProduction.printMethod;
    this.isPrintColorsDisabled = !printMethod;

    if (this.isPrintColorsDisabled) {
      this.localPrintProduction.nbColors = null;
    }

    let isScreenPrintDigital = printMethod === 'SCREENPRINT_DIGITAL';
    this.isShowSecondaryColours = isScreenPrintDigital;
    this.localPrintProduction.secondaryNbColors = isScreenPrintDigital
      ? 'CMYK'
      : null;
  }

  private updatePrintMethodDisabled() {
    this.isPrintMethodDisabled = !this.localPrintProduction.type;
    if (this.isPrintMethodDisabled) {
      this.localPrintProduction.printMethod = null;
      this.updateNbColorsOptions();
      this.updatePrintColorDisabled();
    }
  }

  validateFields(): void {
    this.isFormValid = !!(
      this.localPrintProduction.type &&
      this.localPrintProduction.printMethod &&
      this.localPrintProduction.material &&
      this.localPrintProduction.dimensions &&
      this.localPrintProduction.nbColors &&
      (this.localPrintProduction.dimensions === 'CUSTOM'
        ? this.localPrintProduction.customDimensions.height > 0 &&
          this.localPrintProduction.customDimensions.width > 0
        : true)
    );
  }

  save() {
    this.validateFields();

    if (!this.isFormValid) {
      this.snackService.showError('Vul aub alle verplichte velden in');
      return;
    }

    if (this.localPrintProduction.id) {
      this.printProductionRequestService
        .updatePrintProduction(
          this.localPrintProduction.id,
          this.localPrintProduction
        )
        .subscribe(
          (updatedProduction) => {
            this.dialogRef.close(updatedProduction);
          },
          (error) => {
            console.error('Error updating print production item:', error);
            this.snackService.showError(
              error.status === 422
                ? error.error
                : 'Probleem bij het editeren van een Drukwerk item'
            );
          }
        );
    } else {
      this.printProductionRequestService
        .createPrintProduction(this.localPrintProduction)
        .subscribe(
          (createdProduction) => {
            this.dialogRef.close(createdProduction);
          },
          (error) => {
            console.error('Error creating print production:', error);
            this.snackService.showError(
              error.status === 422
                ? error.error
                : 'Probleem bij het aanmaken van een Drukwerk item'
            );
          }
        );
    }
  }
}
